export type ContextAri = {
	ari: string;
	cloudId?: string;
	resourceOwner?: string;
	workspaceId?: string;
};

export type Installation = {
	contexts: ContextAri[];
	id: string;
};

/**
 * Parses an installation context ARI into a `ContextAri` object.
 *
 * Supported ARI shapes:
 *   - `ari:cloud:<owner>:<cloudId>:workspace/<workspaceId>` → `{ ari, resourceOwner, cloudId, workspaceId }`
 *   - `ari:cloud:<owner>::site/<cloudId>` → `{ ari, resourceOwner, cloudId }`
 *
 * Anything unparseable falls back to `{ ari }` rather than throwing, so an
 * unexpected ARI format never breaks context assembly.
 */
export const parseContextAri = (ari: string): ContextAri => {
	const segments = ari.split(':');

	// Expect `ari:cloud:<owner>:<cloudId>:<resourceType>/<resourceId>`.
	if (segments.length < 5 || segments[0] !== 'ari') {
		return { ari };
	}

	const resourceOwner = segments[2];
	const cloudIdSegment = segments[3];
	const resource = segments[4];
	const [resourceType, resourceId] = resource.split('/');

	if (!resourceType || !resourceId) {
		return { ari };
	}

	const result: ContextAri = { ari };

	if (resourceOwner) {
		result.resourceOwner = resourceOwner;
	}

	if (resourceType === 'site') {
		// Site ARIs encode the cloudId as the resource id and leave segment 4 empty.
		result.cloudId = resourceId;
		return result;
	}

	if (cloudIdSegment) {
		result.cloudId = cloudIdSegment;
	}

	if (resourceType === 'workspace') {
		result.workspaceId = resourceId;
	}

	return result;
};
